import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { UserIcon, CloudArrowUpIcon, XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { FileValidationService } from '../../../../services/fileValidation';
import ErrorDisplay from '../../../common/ErrorDisplay';
import { ErrorHandlingSystem } from '../../../../utils/uploadErrorHandling';
import ProfilePhotoCropper from '../../../common/ProfilePhotoCropper';

const ProfilePictureStep = ({ data, updateData, onNext, onPrevious, isFirstStep }) => {
  const { user } = useAuthContext();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(data.profilePicture || null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showCropper, setShowCropper] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);

  // Clear validation messages when upload starts or succeeds
  useEffect(() => {
    if (isUploading) {
      setValidationErrors([]);
      setValidationWarnings([]);
      setUploadError('');
    }
  }, [isUploading]);

  // Clear validation messages when profile picture changes (successful upload)
  useEffect(() => {
    if (previewUrl) {
      setValidationErrors([]);
      setValidationWarnings([]);
      setUploadError('');
    }
  }, [previewUrl]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Clear previous validation messages
    setValidationErrors([]);
    setValidationWarnings([]);
    setUploadError('');

    // Handle rejected files from dropzone
    if (rejectedFiles.length > 0) {
      const rejectedFile = rejectedFiles[0];
      const errors = rejectedFile.errors.map(error => {
        switch (error.code) {
          case 'file-too-large':
            return {
              code: 'FILE_TOO_LARGE',
              message: `File is too large (${FileValidationService.formatFileSize(rejectedFile.file.size)})`,
              actionableAdvice: `Please reduce file size to under ${FileValidationService.formatFileSize(FileValidationService.MAX_FILE_SIZE)}`
            };
          case 'file-invalid-type':
            return {
              code: 'INVALID_FORMAT',
              message: 'File format is not supported',
              actionableAdvice: 'Please use JPEG, PNG, GIF, or WebP format'
            };
          default:
            return {
              code: 'VALIDATION_ERROR',
              message: error.message,
              actionableAdvice: 'Please try a different file'
            };
        }
      });
      
      const formattedErrors = ErrorHandlingSystem.formatErrors(errors);
      setValidationErrors(formattedErrors);
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      // Validate the file using our comprehensive validation service
      const validationResult = await FileValidationService.validateFile(file);
      
      if (!validationResult.isValid) {
        const formattedErrors = ErrorHandlingSystem.formatErrors(validationResult.errors);
        setValidationErrors(formattedErrors);
        return;
      }

      // Set warnings if any
      if (validationResult.warnings.length > 0) {
        const formattedWarnings = ErrorHandlingSystem.formatErrors(validationResult.warnings);
        setValidationWarnings(formattedWarnings);
      }

      // File is valid, show cropping interface
      setFileToProcess(file);
      setShowCropper(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    multiple: false,
    maxSize: FileValidationService.MAX_FILE_SIZE,
    disabled: isUploading,
    noClick: false,
    noKeyboard: false
  });

  const uploadProfilePicture = async (file) => {
    setIsUploading(true);
    setUploadError('');
    
    try {
      const storage = getStorage();
      const filename = `users/${user.uid}/profile_pictures/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filename);
      
      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update data and preview
      updateData('profilePicture', downloadURL);
      setPreviewUrl(downloadURL);
      
      toast.success('Profile picture uploaded successfully!');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setUploadError('Failed to upload image. Please try again.');
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropComplete = useCallback(async (croppedBlob) => {
    setShowCropper(false);
    setFileToProcess(null);
    
    try {
      if (croppedBlob) {
        // Convert blob to file and upload
        const croppedFile = new File([croppedBlob], fileToProcess?.name || 'cropped-profile.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        
        await uploadProfilePicture(croppedFile);
      }
    } catch (error) {
      console.error('Error processing cropped image:', error);
      setUploadError('Failed to process cropped image. Please try again.');
    }
  }, [fileToProcess]);

  const handleCropCancel = useCallback(() => {
    setShowCropper(false);
    setFileToProcess(null);
    // Clear any existing errors when canceling
    setValidationErrors([]);
    setValidationWarnings([]);
    setUploadError('');
  }, []);

  const removeProfilePicture = () => {
    updateData('profilePicture', null);
    setPreviewUrl(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext();
  };

  return (
    <div className="space-y-8 md:space-y-12">
      <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12">
        {/* Question */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
            Add a profile picture
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
            Upload a photo so members can put a face to your name (optional)
          </p>
        </div>

        {/* Upload Area */}
        <div className="space-y-6">
          {showCropper && fileToProcess ? (
            <ProfilePhotoCropper
              imageFile={fileToProcess}
              onCropComplete={handleCropComplete}
              onCancel={handleCropCancel}
              isModal={false}
            />
          ) : !previewUrl ? (
            <div
              {...getRootProps()}
              className={`relative border-2 border-dashed rounded-2xl p-8 md:p-12 transition-all duration-300 ease-in-out cursor-pointer transform
                ${isDragActive 
                  ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 scale-105 shadow-lg shadow-purple-500/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-102 hover:shadow-md'
                }
                ${isUploading ? 'cursor-not-allowed opacity-75' : ''}
              `}
              role="button"
              tabIndex={0}
              aria-label="Upload profile picture"
              aria-describedby="upload-instructions upload-requirements"
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center justify-center space-y-6">
                <div className="w-32 h-32 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-110">
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-12 w-12 border-b-3 border-purple-500"></div>
                  ) : (
                    <CloudArrowUpIcon className="w-16 h-16 text-purple-500 dark:text-purple-400 transition-transform duration-300 hover:scale-110" />
                  )}
                </div>
                
                {isUploading ? (
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      Uploading...
                    </p>
                    <p className="text-lg text-gray-500 dark:text-gray-400">
                      Please wait while we upload your photo
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div id="upload-instructions">
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                        {isDragActive ? 'Drop your photo here' : 'Drag and drop your photo here'}
                      </p>
                      <p className="text-lg text-gray-500 dark:text-gray-400 mb-3">
                        or click to browse files
                      </p>
                    </div>
                    <div id="upload-requirements" className="space-y-1">
                      <p className="text-base text-gray-400 dark:text-gray-500 font-medium">
                        Supported: JPEG, PNG, GIF, WebP
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Max size: {FileValidationService.formatFileSize(FileValidationService.MAX_FILE_SIZE)}
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Recommended: 400x400px or larger
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-6">
              <div className="relative transform transition-all duration-300 hover:scale-105">
                <img 
                  src={previewUrl} 
                  alt="Profile preview"
                  className="w-40 h-40 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-2xl transition-all duration-300 hover:shadow-3xl"
                />
                <button
                  type="button"
                  onClick={removeProfilePicture}
                  className="absolute -top-2 -right-2 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl hover:scale-110 active:scale-95 focus:ring-4 focus:ring-red-500/20"
                >
                  <XMarkIcon className="w-5 h-5 transition-transform duration-300 hover:rotate-90" />
                </button>
              </div>
              <div className="text-center space-y-3">
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Looking good! You can upload a different photo if you'd like.
                </p>
                <button
                  type="button"
                  onClick={() => setPreviewUrl(null)}
                  className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-lg font-medium transition-all duration-300 underline hover:scale-105 transform active:scale-95"
                >
                  Change photo
                </button>
              </div>
            </div>
          )}

          {/* Enhanced Validation Messages */}
          <ErrorDisplay 
            errors={validationErrors}
            warnings={validationWarnings}
            showRecoveryActions={true}
            collapsible={false}
          />

          {/* Upload Error (from Firebase) */}
          {uploadError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0 text-red-500 dark:text-red-400" />
                <p className="text-red-600 dark:text-red-400 text-sm font-medium">{uploadError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Navigation Buttons */}
        <div className="flex justify-between items-center border-none !border-0">
          {!isFirstStep && (
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 rounded-lg text-lg font-medium text-gray-600 dark:text-gray-400 
                       hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 
                       dark:hover:bg-gray-700 transition-all duration-300 ease-in-out
                       transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-gray-500/20"
            >
              Back
            </button>
          )}
          
          <button
            type="submit"
            className={`px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 ease-in-out transform bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 active:scale-95 focus:ring-4 focus:ring-purple-500/20 ${isFirstStep ? 'ml-auto' : ''}`}
          >
            <span className="flex items-center">
              Continue
              <span className="ml-2 transition-transform duration-300 translate-x-0">
                →
              </span>
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfilePictureStep; 